package top.wcpe.allincore.ui.runtime.element

import top.wcpe.allincore.layout.engine.Measurer
import top.wcpe.allincore.ui.runtime.event.ChangeEvent
import top.wcpe.allincore.ui.runtime.event.MouseEvent
import top.wcpe.allincore.ui.runtime.event.UiEventHandler
import top.wcpe.allincore.ui.runtime.internal.collections.readOnlyCopy
import top.wcpe.allincore.ui.runtime.internal.log.UiLog
import top.wcpe.allincore.ui.runtime.reactive.Effect
import top.wcpe.allincore.ui.runtime.reactive.Signal
import kotlin.jvm.JvmStatic

/**
 * 下拉选择控件。
 *
 * 语义：点击头部展开/收起；展开时显示所有 [DropdownOption]；点击某个 option 写回
 * `selectedValue`、触发 `onChange`、自动收起。
 *
 * **架构选择**：option 子项不进入布局树（[children] 始终返回空 list），
 * 由 `DropdownRenderer` 与 `InputDispatcher` 中 Dropdown 特化分支自绘/自命中。
 * 理由：option 的展开面板需要相对头部 box 在 Y 轴向下偏移并绘制为浮层，
 * 与默认 column 布局语义不符；同时收起态完全隐藏，若交给 layout-engine 还需特殊
 * 处理"占位但不可见"，反不如自绘清晰。
 */
class Dropdown private constructor() : UIElement<Dropdown>() {

    // ===== 核心数据（backing 字段用 Value/Signal/Handler 后缀）=====
    private val optionsValue: MutableList<DropdownOption> = ArrayList()
    private var selectedValueValue: String = ""
    private var expandedValue: Boolean = false
    private var sizeValue: Float = 1f
    private var onChangeHandler: UiEventHandler<ChangeEvent<String>>? = null
    private var boundSelectedValueSignal: Signal<in String>? = null

    // ===== Task 2a：视觉覆盖字段（可空；null = 回退主题 token）=====
    private var textColorValue: Int? = null
    private var arrowColorValue: Int? = null
    private var panelColorValue: Int? = null
    private var optionHoverColorValue: Int? = null
    private var selectedOptionColorValue: Int? = null
    private var selectedOptionTextColorValue: Int? = null
    private var focusBorderColorValue: Int? = null
    private var optionHeightValue: Float? = null
    private var headerColorValue: Int? = null
    private var panelCornerRadiusValue: Float? = null
    private var optionPaddingXValue: Float? = null
    private var optionPaddingYValue: Float? = null

    init {
        this.focusableField = true
        // 默认 onClick：翻转展开态。InputDispatcher 在 Dropdown 上做命中特化时，
        // 若命中到展开面板的某个 option，则会绕过此 onClick 直接选中。
        super.onClick { expanded(!expandedValue) }
    }

    // ===== CRTP 自引用 =====
    override fun self(): Dropdown = this

    /**
     * 覆写 onClick：先翻转展开态，再执行用户传入的 handler。
     */
    override fun onClick(handler: UiEventHandler<MouseEvent>?): Dropdown {
        super.onClick { e: MouseEvent ->
            expanded(!expandedValue)
            handler?.accept(e)
        }
        return this
    }

    /**
     * 覆写 child：DropdownOption 子项被收集到内部列表而非布局树。
     */
    override fun child(child: UIElement<*>): Dropdown {
        if (child is DropdownOption) {
            optionsValue.add(child)
        } else {
            super.child(child)
        }
        return this
    }

    /**
     * 故意返回不可变空 list：option 子项不参与默认布局/事件分发，由 Dropdown 自管。
     * 使用 [options] 访问 option 列表。
     */
    override fun children(): List<UIElement<*>> = emptyList()

    /** 仅供测试 / Renderer 访问的 option 列表（不可变视图）。 */
    // 只读拷贝（readOnlyCopy 契约）：与迁移前 Collections.unmodifiableList 行为等价
    fun options(): List<DropdownOption> = readOnlyCopy(optionsValue)

    /**
     * 清空并重建 options 列表。
     * 供 IrInterpreter 的 items 响应式 Effect 调用——state 列表变化时
     * diff 比较后全量替换，避免逐条 add/remove 导致中间态闪烁。
     */
    fun rebuildOptions(newOptions: List<DropdownOption>?) {
        optionsValue.clear()
        if (newOptions != null) {
            optionsValue.addAll(newOptions)
        }
    }

    fun selectedValue(v: String?): Dropdown {
        val n = v ?: ""
        val old = selectedValueValue
        selectedValueValue = n
        if (old != n) {
            boundSelectedValueSignal?.set(n)
            onChangeHandler?.let { handler ->
                // 隔离：DSL onChange 回调异常不得冒泡到输入派发线程。
                try {
                    handler.accept(ChangeEvent(old, n))
                } catch (t: Throwable) {
                    logger.warn("[ui] Dropdown(id=${id()}) onChange 回调异常已隔离；旧值=$old 新值=$n", t)
                }
            }
        }
        return this
    }

    fun selectedValue(): String = selectedValueValue

    /**
     * 获取当前选中项的显示文本（label）。
     * 遍历 options 找到 value 匹配的项，返回其 label()；未找到时回退到 selectedValue 本身。
     */
    fun selectedOptionLabel(): String {
        for (opt in optionsValue) {
            if (opt.value() == selectedValueValue) {
                return opt.label()
            }
        }
        return selectedValueValue
    }

    fun size(size: Float): Dropdown {
        // 非正字号无意义且会导致行高塌缩/负值，回退到默认 1f（与 Button.textScale 保持一致）
        sizeValue = if (size <= 0f) 1f else size
        return this
    }

    fun size(): Float = sizeValue

    /**
     * 实际 option 行高（像素）：`optionHeight` 覆盖值优先（绝对像素，不乘 size），
     * 未覆盖时用常量 [OPTION_HEIGHT] 随 size 成比例缩放。
     * 渲染（DropdownRenderer）/ 命中（HitTester、ControlInteractions）/ 测尺（measurer）
     * 三条链统一走本方法，保证行高一致。
     */
    fun effectiveOptionHeight(): Float = optionHeightValue ?: (OPTION_HEIGHT * sizeValue)

    /**
     * 为布局引擎提供头部的内禀高度：高度 = [effectiveOptionHeight]。
     * 宽度透传已知宽度（通常由用户显式设置 width）。
     *
     * 当用户未显式设置 height 时，布局引擎将自动使用此值，
     * 使头部高度随 size 缩放，无需手动调大 height。
     *
     * 若用户显式设置了 height，Taffy 会优先使用显式值，measurer 对高度无效。
     */
    override fun measurer(): Measurer = Measurer { kw, _ -> floatArrayOf(kw, effectiveOptionHeight()) }

    fun expanded(v: Boolean): Dropdown {
        expandedValue = v
        return this
    }

    fun expanded(): Boolean = expandedValue

    fun onChange(handler: UiEventHandler<ChangeEvent<String>>): Dropdown {
        onChangeHandler = handler
        return this
    }

    /** 双向绑定到响应式 Signal。 */
    fun bindSelectedValue(signal: Signal<in String>): Dropdown {
        // 逆变投影 Signal<in String>：接收任意可写入 String 的信号，写入位放开可直接 set(String)，
        // 读取位投影为 Any? 交由 toStringValue 解码——无需强转。
        boundSelectedValueSignal = signal
        // 绑定值解析失败时刻意保留 fail-fast：异常向上抛由响应式写入点之上的入口安全网兜底。
        lifecycleScopeField.register(Effect { selectedValueValue = toStringValue(signal.get()) })
        return this
    }

    /**
     * 由 InputDispatcher 调用：选中给定 option 并自动收起。
     */
    fun selectOption(option: DropdownOption?) {
        if (option == null) return
        selectedValue(option.value())
        expanded(false)
    }

    // ===== Task 2a：视觉覆盖 fluent setter / getter ====
    // 方法引用形如 `Dropdown::textColor` 由 Java ControlVisualPropContributor 消费。

    fun textColor(v: Int?): Dropdown {
        textColorValue = v
        return this
    }
    fun textColor(): Int? = textColorValue
    fun arrowColor(v: Int?): Dropdown {
        arrowColorValue = v
        return this
    }
    fun arrowColor(): Int? = arrowColorValue
    fun panelColor(v: Int?): Dropdown {
        panelColorValue = v
        return this
    }
    fun panelColor(): Int? = panelColorValue
    fun optionHoverColor(v: Int?): Dropdown {
        optionHoverColorValue = v
        return this
    }
    fun optionHoverColor(): Int? = optionHoverColorValue
    fun selectedOptionColor(v: Int?): Dropdown {
        selectedOptionColorValue = v
        return this
    }
    fun selectedOptionColor(): Int? = selectedOptionColorValue
    fun selectedOptionTextColor(v: Int?): Dropdown {
        selectedOptionTextColorValue = v
        return this
    }
    fun selectedOptionTextColor(): Int? = selectedOptionTextColorValue
    fun focusBorderColor(v: Int?): Dropdown {
        focusBorderColorValue = v
        return this
    }
    fun focusBorderColor(): Int? = focusBorderColorValue
    fun optionHeight(v: Float?): Dropdown {
        optionHeightValue = v
        markLayoutDirty() // FR-30 B1：选项高度影响 measurer 测尺
        return this
    }
    fun optionHeight(): Float? = optionHeightValue
    fun headerColor(v: Int?): Dropdown {
        headerColorValue = v
        return this
    }
    fun headerColor(): Int? = headerColorValue
    fun panelCornerRadius(v: Float?): Dropdown {
        panelCornerRadiusValue = v
        return this
    }
    fun panelCornerRadius(): Float? = panelCornerRadiusValue
    fun optionPaddingX(v: Float?): Dropdown {
        optionPaddingXValue = v
        return this
    }
    fun optionPaddingX(): Float? = optionPaddingXValue
    /** option 文字距行顶的内边距（像素）：覆盖后取代默认垂直居中；null = 保持居中。 */
    fun optionPaddingY(v: Float?): Dropdown {
        optionPaddingYValue = v
        return this
    }
    fun optionPaddingY(): Float? = optionPaddingYValue

    companion object {
        /** 单个 option 行的高度（px），与 DropdownRenderer / InputDispatcher 命中保持一致。 */
        const val OPTION_HEIGHT: Float = 14f

        private val logger = UiLog("top.wcpe.allincore.ui.runtime.element.Dropdown")

        /** 创建新的下拉选择控件。 */
        @JvmStatic
        fun create(): Dropdown = Dropdown()

        /** 将 Signal 值安全转为字符串；null → ""。 */
        private fun toStringValue(v: Any?): String = v?.toString() ?: ""
    }
}
